import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export interface UploadedPrescriptionFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: Date;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  upload_id?: string;
  confidence?: number;
  review_status?: string;
}

interface PrescriptionUploadContextType {
  files: UploadedPrescriptionFile[];
  addFile: (file: UploadedPrescriptionFile) => void;
  updateFile: (fileId: string, updates: Partial<UploadedPrescriptionFile>) => void;
  removeFile: (fileId: string) => void;
  clearFiles: () => void;
}

const PrescriptionUploadContext = createContext<PrescriptionUploadContextType | undefined>(undefined);

export function PrescriptionUploadProvider({ children }: { children: ReactNode }) {
  const [files, setFiles] = useState<UploadedPrescriptionFile[]>([]);

  const addFile = useCallback((file: UploadedPrescriptionFile) => {
    setFiles((prev) => [...prev, file]);
  }, []);

  const updateFile = useCallback((fileId: string, updates: Partial<UploadedPrescriptionFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f)));
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  return (
    <PrescriptionUploadContext.Provider
      value={{ files, addFile, updateFile, removeFile, clearFiles }}
    >
      {children}
    </PrescriptionUploadContext.Provider>
  );
}

export function usePrescriptionUpload() {
  const context = useContext(PrescriptionUploadContext);
  if (context === undefined) {
    throw new Error('usePrescriptionUpload must be used within a PrescriptionUploadProvider');
  }
  return context;
}
